import { createFileRoute, Link } from '@tanstack/react-router'
import { useRef, useState, useEffect, type RefObject } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { submitDemoRequestApi } from '@/lib/api'
import { toast } from 'sonner'
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ShieldIcon,
  UsersIcon,
  BanknoteIcon,
  SmartphoneIcon,
  LaptopIcon,
  TrendingUpIcon,
  EyeOffIcon,
  RefreshCwIcon,
  ZapIcon,
  ChevronDownIcon,
  PiggyBankIcon,
  BadgeDollarSignIcon,
  LockIcon,
  ScaleIcon,
  UserPlusIcon,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────
// Scroll-reveal hook
// ─────────────────────────────────────────────────────────────
function useInView(ref: RefObject<HTMLElement | null>, opts?: IntersectionObserverInit) {
  const [inView, setInView] = useState(false)
  const threshold = opts?.threshold ?? 0.1
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.unobserve(el)
        }
      },
      { threshold },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, threshold])
  return inView
}

function RevealSection({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref)
  return (
    <section
      id={id}
      ref={ref}
      className={`transition-all duration-700 ease-out will-change-transform ${
        inView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      } ${className}`}
    >
      {children}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────
// Section spacing constants
// ─────────────────────────────────────────────────────────────
const sectionPy = 'py-20 sm:py-28 lg:py-36'
const containerW = 'mx-auto max-w-7xl px-6 sm:px-8 lg:px-12'

// Mid-point of the Deloitte $1,500–$2,000 per-employee annual productivity loss.
const STRESS_COST_PER_EMPLOYEE = 1750

// ─────────────────────────────────────────────────────────────
// Reusable 0% highlight badge
// ─────────────────────────────────────────────────────────────
function ZeroBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#00E5A0] px-2.5 py-0.5 text-xs font-bold text-[#0B1120] shadow-sm shadow-[#00E5A0]/30">
      {label}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────
// 1. HERO
// ─────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="relative min-h-svh flex items-center overflow-hidden bg-[#0B1120] pt-24 pb-16 sm:pt-28 sm:pb-20 lg:pt-36 lg:pb-28">
      {/* Subtle grid pattern overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Decorative glow orbs */}
      <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />

      <div className={`${containerW} relative z-10 w-full`}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="max-w-xl">
            {/* Eyebrow badge */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#00E5A0]/30 bg-[#00E5A0]/10 px-4 py-1.5 text-xs font-medium text-[#00E5A0]">
              <ZapIcon className="size-3.5" />
              Employer-paid employee benefit
            </div>

            <h1 className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-[1.08] tracking-tight text-white">
              Your team pools money.{' '}
              <span className="text-[#00E5A0]">Takes turns. Everyone gets funded.</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-lg">
              PeerPass lets your employees lend each other money &mdash; anonymously, at 0% interest
              &mdash; through the payroll you already run. No company funds required.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#book-demo">
                <Button
                  size="lg"
                  className="bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base px-8 py-6 shadow-xl shadow-[#00E5A0]/25"
                >
                  See it in action
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </a>
              <a href="#how-it-works">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white text-base px-8 py-6"
                >
                  How funding circles work &darr;
                </Button>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm text-white/50">
              <span className="flex items-center gap-1.5">
                <ZeroBadge label="0% Interest" />
                for employees
              </span>
              <span className="flex items-center gap-1.5">
                <EyeOffIcon className="size-4 text-[#00E5A0]" />
                100% Anonymous
              </span>
              <span className="flex items-center gap-1.5">
                <BanknoteIcon className="size-4 text-[#00E5A0]" />
                Employer-paid subscription
              </span>
            </div>
          </div>

          {/* Right: Funding Circle phone mockup */}
          <div className="hidden lg:flex justify-center relative">
            <div className="relative w-[280px] h-[560px] rounded-[3rem] border-4 border-white/10 bg-[#0D1B2A] shadow-2xl shadow-black/50 overflow-hidden">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-2xl z-10" />
              {/* Screen content */}
              <div className="absolute inset-4 top-10 rounded-2xl bg-[#132233] p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-white">Funding Circle</div>
                  <span className="rounded-full bg-[#00E5A0]/15 px-2 py-0.5 text-[9px] font-bold text-[#00E5A0]">
                    0% INTEREST
                  </span>
                </div>

                {/* Lump-sum panel */}
                <div className="rounded-xl bg-[#00E5A0]/10 border border-[#00E5A0]/20 p-3">
                  <div className="text-[10px] text-white/40">Your payout (Round 2)</div>
                  <div className="text-2xl font-semibold text-[#00E5A0]">$2,500</div>
                  <div className="mt-1 text-[10px] text-white/40">5 peers &middot; $500/cycle &middot; 0% interest</div>
                </div>

                {/* Rotation progress */}
                <div className="space-y-1.5">
                  <div className="text-[10px] font-medium text-white/50">Rotation progress</div>
                  {[
                    { who: 'Peer 1', done: true },
                    { who: 'You', done: false, current: true },
                    { who: 'Peer 3', done: false },
                    { who: 'Peer 4', done: false },
                    { who: 'Peer 5', done: false },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div
                        className={`size-5 rounded-full flex items-center justify-center text-[8px] font-bold ${
                          row.done
                            ? 'bg-[#00E5A0] text-[#0B1120]'
                            : row.current
                              ? 'bg-[#00E5A0]/30 text-[#00E5A0] ring-2 ring-[#00E5A0]/40'
                              : 'bg-white/10 text-white/30'
                        }`}
                      >
                        {row.done ? <CheckCircleIcon className="size-3" /> : i + 1}
                      </div>
                      <span className={`text-[10px] ${row.current ? 'text-white font-medium' : 'text-white/40'}`}>
                        {row.who}
                      </span>
                      {row.current && <span className="ml-auto text-[8px] text-[#00E5A0]">Receiving now</span>}
                    </div>
                  ))}
                </div>

                {/* Auto-deduct footer */}
                <div className="mt-auto flex items-center gap-2 rounded-xl bg-white/5 p-2.5">
                  <RefreshCwIcon className="size-3.5 text-[#00E5A0]" />
                  <div className="text-[10px] text-white/50">Auto-deducted via payroll &middot; no missed payments</div>
                </div>
              </div>
            </div>

            {/* Floating stat card */}
            <div className="absolute -bottom-4 -left-8 rounded-2xl border border-white/10 bg-[#0D1B2A]/90 backdrop-blur-md px-5 py-3 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="size-3 rounded-full bg-[#00E5A0] animate-pulse" />
                <span className="text-xs text-white/70">Active pool: $12.4k</span>
              </div>
            </div>

            {/* Floating stat card 2 */}
            <div className="absolute -top-4 -right-8 rounded-2xl border border-white/10 bg-[#0D1B2A]/90 backdrop-blur-md px-5 py-3 shadow-lg">
              <div className="flex items-center gap-2 text-[#00E5A0]">
                <PiggyBankIcon className="size-4" />
                <span className="text-xs font-medium text-white">0% interest for employees</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="mt-16 flex justify-center animate-bounce">
          <ChevronDownIcon className="size-6 text-white/30" />
        </div>
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────
// 2. PROBLEM + interactive calculator
// ─────────────────────────────────────────────────────────────
function ProblemSection() {
  const [employees, setEmployees] = useState(250)
  const loss = employees * STRESS_COST_PER_EMPLOYEE

  const fmt = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

  return (
    <RevealSection className={`${sectionPy} bg-white`}>
      <div className={containerW}>
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">The Problem</p>
          <h2 className="mt-4 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-[#0B1120]">
            Your best employee is losing $2,000 a year to financial stress.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#64748B] leading-relaxed">
            Traditional employee loans eat into your budget. Payday lenders trap your team in debt.
            There&rsquo;s a better way &mdash; one that costs your company nothing to lend.
          </p>
        </div>

        <div className="mt-12 grid gap-8 sm:grid-cols-3">
          {/* Stat 1 */}
          <div className="rounded-2xl bg-[#F8FAFC] p-8 text-center">
            <div className="flex items-center justify-center gap-2">
              <TrendingUpIcon className="size-6 text-[#00E5A0]" />
              <div className="text-4xl sm:text-5xl font-bold text-[#0B1120]">$1.5k&ndash;$2k</div>
            </div>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed">
              <span className="font-semibold text-[#0B1120]">Lost productivity</span> per employee
              annually from financial stress &amp; absenteeism.{' '}
              <span className="text-[#94A3B8]">&mdash; Deloitte</span>
            </p>
          </div>
          {/* Stat 2 */}
          <div className="rounded-2xl bg-[#F8FAFC] p-8 text-center">
            <div className="flex items-center justify-center gap-2">
              <UsersIcon className="size-6 text-[#00E5A0]" />
              <div className="text-4xl sm:text-5xl font-bold text-[#0B1120]">64%</div>
            </div>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed">
              of employees say financial stress negatively impacts their work, and the financially
              strained are <span className="font-semibold text-[#0B1120]">2&times; as likely</span> to
              job-hunt.{' '}
              <span className="text-[#94A3B8]">&mdash; PwC</span>
            </p>
          </div>
          {/* Stat 3 */}
          <div className="rounded-2xl bg-[#F8FAFC] p-8 text-center">
            <div className="flex items-center justify-center gap-2">
              <BadgeDollarSignIcon className="size-6 text-[#00E5A0]" />
              <div className="text-4xl sm:text-5xl font-bold text-[#0B1120]">1 in 3</div>
            </div>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed">
              employees would consider a high-interest <span className="font-semibold text-[#0B1120]">payday loan</span>{' '}
              in an emergency, trapping them in a debt cycle.{' '}
              <span className="text-[#94A3B8]">&mdash; CFPB</span>
            </p>
          </div>
        </div>

        {/* Interactive productivity-loss calculator */}
        <div className="mx-auto mt-12 max-w-2xl rounded-3xl border border-[#E2E8F0] bg-gradient-to-br from-[#F8FAFC] to-white p-8 sm:p-10">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">
              See your cost
            </p>
            <h3 className="mt-3 text-xl sm:text-2xl font-semibold text-[#0B1120]">
              How much does financial stress cost your company?
            </h3>
          </div>

          <div className="mt-8 flex flex-col items-center gap-2">
            <label htmlFor="emp-count" className="text-sm font-medium text-[#64748B]">
              Number of employees
            </label>
            <div className="flex items-center gap-3">
              <input
                id="emp-count"
                type="number"
                min={1}
                max={100000}
                value={employees}
                onChange={(e) => setEmployees(Math.max(1, Math.min(100000, Number(e.target.value) || 1)))}
                className="w-24 rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-center text-lg font-semibold text-[#0B1120] outline-none focus-visible:border-[#00E5A0] focus-visible:ring-2 focus-visible:ring-[#00E5A0]/30"
              />
              <span className="text-sm text-[#94A3B8]">or drag the slider</span>
            </div>
            <input
              type="range"
              min={10}
              max={5000}
              step={10}
              value={Math.min(employees, 5000)}
              onChange={(e) => setEmployees(Number(e.target.value))}
              className="mt-2 w-full max-w-md accent-[#00E5A0]"
              aria-label="Adjust employee count"
            />
          </div>

          <div className="mt-8 rounded-2xl bg-[#0B1120] px-6 py-7 text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-white/40">
              Estimated annual productivity loss
            </p>
            <div className="mt-2 text-4xl sm:text-5xl font-bold text-[#00E5A0] tabular-nums">
              {fmt(loss)}
            </div>
            <p className="mt-3 text-xs text-white/40">
              Based on the Deloitte midpoint of ${STRESS_COST_PER_EMPLOYEE.toLocaleString()}/employee/year.
              PeerPass directly combats this drain.
            </p>
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-2xl text-center">
          <p className="text-base sm:text-lg font-semibold text-[#0B1120]">
            Your team needs access to capital. They shouldn&rsquo;t have to pay 400% interest to get it.
          </p>
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 3. HOW IT WORKS — with animated ROSCA circle visual
// ─────────────────────────────────────────────────────────────
const steps = [
  {
    number: '01',
    title: 'Create a Circle',
    subtitle: 'The Employee App',
    description:
      'An employee needs funds. They create a “Funding Circle,” setting the contribution amount and the limited number of peers required to join.',
    icon: SmartphoneIcon,
  },
  {
    number: '02',
    title: 'Fund & Rotate',
    subtitle: 'Anonymous Pooling',
    description:
      'Colleagues join the circle. Each pay cycle everyone contributes their set amount. One participant receives the total pooled lump sum. The next cycle the funds rotate to the next person, until everyone has been paid. Zero interest. Zero fees.',
    icon: RefreshCwIcon,
  },
  {
    number: '03',
    title: 'Automate & Guarantee',
    subtitle: 'The Employer Portal',
    description:
      'The employer acts as the guarantor. Contributions and payouts are seamlessly integrated into your existing payroll system. Repayments are deducted automatically at the source. No chasing, no defaults, no awkwardness.',
    icon: ShieldIcon,
  },
]

// Animated ROSCA diagram — 5 peers around a central pool; the recipient
// highlight orbits to show rotation over time. Counter-rotation keeps the
// node icons upright. All animation is disabled under prefers-reduced-motion
// (gated in globals.css).
function RoscaDiagram() {
  const peers = [0, 1, 2, 3, 4]
  const radius = 120
  const halfIcon = 24 // half of size-12 (48px) — centers avatar on orbit point
  return (
    <div className="relative mx-auto size-[260px] sm:size-[300px]">
      {/* Orbiting wrapper — moves the recipient highlight around the circle.
          Positioning uses left/top (not transform) so child transform
          animations never conflict with layout offsets. */}
      <div className="pp-rotate-recipient absolute inset-0">
        {peers.map((i) => {
          const angle = (i / peers.length) * 2 * Math.PI - Math.PI / 2
          const x = Math.cos(angle) * radius
          const y = Math.sin(angle) * radius
          return (
            <div
              key={i}
              className="absolute"
              style={{
                left: `calc(50% + ${x}px - ${halfIcon}px)`,
                top: `calc(50% + ${y}px - ${halfIcon}px)`,
              }}
            >
              {/* Counter-rotate keeps the avatar upright while orbiting.
                  No translate utilities here — centering is handled by the
                  pixel offsets above, so the rotate animation is the only
                  transform on this element. */}
              <div className="pp-counter-rotate relative">
                <div
                  className={`flex size-12 items-center justify-center rounded-full border-2 ${
                    i === 0
                      ? 'pp-recipient-ring border-[#00E5A0] bg-[#00E5A0] text-[#0B1120]'
                      : 'border-white/20 bg-[#132233] text-white/60'
                  }`}
                >
                  {i === 0 ? (
                    <BanknoteIcon className="size-5" />
                  ) : (
                    <UsersIcon className="size-5" />
                  )}
                </div>
                {i === 0 && (
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#00E5A0] px-2 py-0.5 text-[9px] font-bold text-[#0B1120]">
                    RECEIVING
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Central pool — centering (translate) on the outer element, scale
          animation on the inner element, so the two never collide. */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="pp-pool-glow flex size-20 flex-col items-center justify-center rounded-full bg-[#00E5A0]/15 border border-[#00E5A0]/30">
          <span className="text-[10px] font-medium text-white/50">Pool</span>
          <span className="text-base font-bold text-[#00E5A0]">$2,500</span>
        </div>
      </div>

      {/* Contribution coins pulsing at contribution points (decorative).
          Positioned via left/top with pre-computed x/y so the pp-coin-in
          scale animation is the only transform on each coin. */}
      <div className="pointer-events-none absolute inset-0">
        {[0, 72, 144, 216, 288].map((deg, i) => {
          const rad = (deg * Math.PI) / 180 - Math.PI / 2
          const coinR = 70
          const cx = Math.cos(rad) * coinR
          const cy = Math.sin(rad) * coinR
          return (
            <div
              key={i}
              className="pp-coin-in absolute size-2 rounded-full bg-[#00E5A0]/60"
              style={{
                left: `calc(50% + ${cx}px - 4px)`,
                top: `calc(50% + ${cy}px - 4px)`,
                animationDelay: `${i * 0.28}s`,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

function HowItWorksSection() {
  return (
    <RevealSection id="how-it-works" className={`${sectionPy} bg-[#F8FAFC]`}>
      <div className={containerW}>
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">The Solution</p>
          <h2 className="mt-4 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-[#0B1120]">
            How funding circles work
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#64748B] leading-relaxed">
            A modern take on rotating credit. Employees pool their money, take turns receiving
            the full lump sum, and repay at 0% interest &mdash; all through your payroll.
          </p>
        </div>

        {/* Animated ROSCA visual — the "Aha!" moment */}
        <div className="mt-14 flex flex-col items-center">
          <div className="rounded-3xl border border-[#E2E8F0] bg-[#0B1120] px-6 py-10 sm:px-16 sm:py-14">
            <RoscaDiagram />
            <p className="mt-8 text-center text-sm text-white/50 max-w-sm mx-auto">
              5 peers each contribute $500 per cycle. Each cycle, one person receives the full $2,500
              lump sum &mdash; at <ZeroBadge label="0% interest" />. The rotation continues until
              everyone has been paid.
            </p>
          </div>
        </div>

        {/* Step cards */}
        <div className="mt-16 grid gap-8 md:grid-cols-3 relative">
          {/* Connecting line (desktop) */}
          <div className="pointer-events-none absolute top-12 left-[calc(16.67%+1.5rem)] right-[calc(16.67%+1.5rem)] hidden md:block">
            <div className="h-px w-full bg-gradient-to-r from-[#00E5A0]/60 via-[#00E5A0]/30 to-[#00E5A0]/60" />
          </div>

          {steps.map((step) => (
            <div key={step.number} className="relative flex flex-col items-center text-center group">
              {/* Number circle */}
              <div className="relative z-10 flex size-24 items-center justify-center rounded-full border-2 border-[#00E5A0]/20 bg-white shadow-lg shadow-[#00E5A0]/5 transition-all duration-300 group-hover:border-[#00E5A0]/50 group-hover:shadow-[#00E5A0]/20">
                <div className="flex size-16 items-center justify-center rounded-full bg-[#00E5A0]/10 text-[#00E5A0] transition-all duration-300 group-hover:bg-[#00E5A0] group-hover:text-white">
                  <step.icon className="size-7" />
                </div>
              </div>

              {/* Label */}
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#00E5A0]/10 px-3 py-1">
                <span className="text-xs font-bold text-[#00E5A0]">{step.number}</span>
              </div>
              <h3 className="mt-3 text-xl font-bold text-[#0B1120]">{step.title}</h3>
              <p className="text-sm font-medium text-[#00E5A0]">{step.subtitle}</p>
              <p className="mt-3 text-sm text-[#64748B] leading-relaxed max-w-xs">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 4. EMPLOYER BENEFITS
// ─────────────────────────────────────────────────────────────
const employerBenefits = [
  {
    title: '$2,000 back per employee',
    description:
      'Financial stress costs each employee $1,500\u2013$2,000/year in lost productivity. PeerPass directly combats that drain.',
    icon: TrendingUpIcon,
  },
  {
    title: 'Your balance sheet stays untouched',
    description:
      'The capital comes from employees\u2019 own peer pool. You provide the infrastructure \u2014 not the money.',
    icon: ShieldIcon,
  },
  {
    title: 'One flat subscription fee',
    description:
      'No interest markups, no commissions, no surprise costs. Predictable pricing you can budget for.',
    icon: BanknoteIcon,
  },
  {
    title: 'Repayment is automatic',
    description:
      'Contributions are deducted through payroll before the employee gets their net pay. No chasing, no defaults.',
    icon: RefreshCwIcon,
  },
  {
    title: 'Stand out in a tight job market',
    description:
      'Offer a benefit your competitors don\u2019t have. Show candidates you care about their financial health.',
    icon: UserPlusIcon,
  },
]

function EmployerBenefitsSection() {
  return (
    <RevealSection className={`${sectionPy} bg-white`}>
      <div className={containerW}>
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">For Employers</p>
          <h2 className="mt-4 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-[#0B1120]">
            A benefit your team will actually use.
            <br />
            <span className="text-[#00E5A0]">That costs you nothing to lend.</span>
          </h2>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {employerBenefits.map((benefit) => (
            <div
              key={benefit.title}
              className="group rounded-2xl border border-[#E2E8F0] bg-white p-6 transition-all duration-300 hover:border-[#00E5A0]/30 hover:shadow-lg hover:shadow-[#00E5A0]/5 hover:-translate-y-1"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-[#00E5A0]/10 text-[#00E5A0] transition-all duration-300 group-hover:bg-[#00E5A0] group-hover:text-white">
                <benefit.icon className="size-6" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-[#0B1120]">{benefit.title}</h3>
              <p className="mt-2 text-sm text-[#64748B] leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 5. EMPLOYEE BENEFITS
// ─────────────────────────────────────────────────────────────
const employeeBenefits = [
  {
    title: '0% interest. Always.',
    description:
      'No fees, no commissions, no hidden charges. Every dollar you contribute comes back to you.',
    icon: BadgeDollarSignIcon,
  },
  {
    title: 'Your finances stay private',
    description:
      'No one in your circle knows who else is in it. No awkward conversations with HR or your manager.',
    icon: EyeOffIcon,
  },
  {
    title: 'Savings without thinking about it',
    description:
      'The rotating model builds a savings habit naturally. You\u2019re paying yourself \u2014 it just doesn\u2019t feel like it.',
    icon: PiggyBankIcon,
  },
  {
    title: 'Never miss a payment',
    description:
      'It\u2019s deducted from your paycheck automatically. No late fees, no credit score hits, no reminders.',
    icon: RefreshCwIcon,
  },
]

function EmployeeBenefitsSection() {
  return (
    <RevealSection className={`${sectionPy} bg-[#F8FAFC]`}>
      <div className={containerW}>
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">For Employees</p>
          <h2 className="mt-4 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-[#0B1120]">
            Borrow from your peers.{' '}
            <span className="text-[#00E5A0]">Not from payday lenders.</span>
          </h2>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {employeeBenefits.map((benefit) => (
            <div
              key={benefit.title}
              className="group flex gap-5 rounded-2xl border border-[#E2E8F0] bg-white p-6 transition-all duration-300 hover:border-[#00E5A0]/30 hover:shadow-lg hover:shadow-[#00E5A0]/5"
            >
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#00E5A0]/10 text-[#00E5A0] transition-all duration-300 group-hover:bg-[#00E5A0] group-hover:text-white">
                <benefit.icon className="size-6" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#0B1120]">{benefit.title}</h3>
                <p className="mt-1 text-sm text-[#64748B] leading-relaxed">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 6. PLATFORM FEATURES (Dual Interface)
// ─────────────────────────────────────────────────────────────
function PlatformFeaturesSection() {
  return (
    <RevealSection id="features" className={`${sectionPy} bg-white`}>
      <div className={containerW}>
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">Platform</p>
          <h2 className="mt-4 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-[#0B1120]">
            Works with the payroll system you already use.
          </h2>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Mobile app */}
          <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3">
              <div className="flex size-12 items-center justify-center rounded-xl bg-[#0B1120] text-white">
                <SmartphoneIcon className="size-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0B1120]">The Employee Mobile App</h3>
                <p className="text-sm text-[#64748B]">Available on iOS &amp; Android</p>
              </div>
            </div>

            <ul className="mt-8 space-y-4">
              {[
                'Intuitive interface to create, join, and manage Funding Circles',
                'Blind-matching algorithm to ensure absolute anonymity among peers',
                'Real-time tracking of circle rotation, contributions, and payout schedules',
                'In-app financial literacy tools and circle management alerts',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-[#00E5A0]" />
                  <span className="text-sm text-[#475569]">{item}</span>
                </li>
              ))}
            </ul>

            {/* Mini phone mockup */}
            <div className="mt-8 flex justify-center">
              <div className="w-[180px] h-[340px] rounded-[2rem] border-4 border-[#0B1120]/10 bg-white shadow-inner overflow-hidden">
                <div className="bg-[#0B1120] p-3 h-full flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-white">Circle</div>
                    <div className="size-2 rounded-full bg-[#00E5A0]" />
                  </div>
                  <div className="flex-1 rounded-xl bg-white/10 p-3 flex flex-col gap-2">
                    <div className="h-2 w-3/4 rounded-full bg-white/20" />
                    <div className="h-2 w-1/2 rounded-full bg-white/20" />
                    <div className="mt-auto h-8 rounded-lg bg-[#00E5A0]/40 flex items-center justify-center">
                      <span className="text-[8px] font-semibold text-white">Join Circle &middot; 0%</span>
                    </div>
                  </div>
                  <div className="rounded-xl bg-white/5 p-2 flex items-center gap-2">
                    <div className="size-6 rounded-full bg-[#00E5A0]/30" />
                    <div className="text-[8px] text-white/50">5 peers &middot; rotating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Employer portal */}
          <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3">
              <div className="flex size-12 items-center justify-center rounded-xl bg-[#0B1120] text-white">
                <LaptopIcon className="size-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0B1120]">The Employer &amp; Admin Portal</h3>
                <p className="text-sm text-[#64748B]">Web-based dashboard</p>
              </div>
            </div>

            <ul className="mt-8 space-y-4">
              {[
                'High-level analytics dashboard to track company-wide financial wellness adoption (without breaching individual privacy)',
                'Role-based access control and compliance management',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-[#00E5A0]" />
                  <span className="text-sm text-[#475569]">{item}</span>
                </li>
              ))}
            </ul>

            {/* Mini laptop mockup */}
            <div className="mt-8 flex justify-center">
              <div className="w-full max-w-[320px]">
                {/* Screen */}
                <div className="rounded-t-lg border-2 border-[#0B1120]/10 bg-[#0B1120] p-4 h-44 overflow-hidden">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="size-2 rounded-full bg-red-400" />
                    <div className="size-2 rounded-full bg-yellow-400" />
                    <div className="size-2 rounded-full bg-[#00E5A0]" />
                    <div className="ml-2 h-2 w-16 rounded-full bg-white/20" />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-20 space-y-2">
                      <div className="h-2 rounded-full bg-white/10" />
                      <div className="h-2 rounded-full bg-white/10" />
                      <div className="h-2 rounded-full bg-[#00E5A0]/30" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex justify-between">
                        <div className="h-3 w-16 rounded bg-[#00E5A0]/20" />
                        <div className="h-3 w-12 rounded bg-white/10" />
                      </div>
                      <div className="h-16 rounded-lg border border-white/10 p-2">
                        <div className="flex justify-between">
                          <div className="h-1.5 w-12 rounded bg-white/20" />
                          <div className="h-1.5 w-8 rounded bg-[#00E5A0]/40" />
                        </div>
                        <div className="mt-2 h-1.5 w-20 rounded bg-white/20" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Base */}
                <div className="mx-auto h-2 w-[60%] rounded-b-lg bg-[#0B1120]/10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 7. SECURITY & TRUST
// ─────────────────────────────────────────────────────────────
function SecuritySection() {
  const items = [
    {
      icon: EyeOffIcon,
      title: 'Peers never see each other',
      body: 'Our matching algorithm ensures circle members stay anonymous. No names, no identities, no awkwardness.',
    },
    {
      icon: RefreshCwIcon,
      title: 'Payroll handles everything',
      body: 'Contributions and payouts flow through your existing payroll provider. No manual transfers, no chasing.',
    },
    {
      icon: LockIcon,
      title: 'Encrypted end to end',
      body: 'AES-256 at rest, TLS 1.3 in transit. SOC 2 Type II ready. Your data stays yours.',
    },
    {
      icon: ScaleIcon,
      title: 'Fully compliant',
      body: 'Structured as a rotating credit association. We don\u2019t hold funds \u2014 so no money transmitter license needed.',
    },
  ]

  return (
    <RevealSection className={`${sectionPy} bg-[#0B1120]`}>
      <div className={containerW}>
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">Security</p>
          <h2 className="mt-4 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-white">
            Your data is encrypted.
            <br />
            <span className="text-[#00E5A0]">Your circles are anonymous.</span>
          </h2>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <div
              key={item.title}
              className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur-sm transition-all duration-300 hover:border-[#00E5A0]/30 hover:bg-white/[0.07]"
            >
              <div className="mx-auto flex size-16 items-center justify-center rounded-2xl bg-[#00E5A0]/10 text-[#00E5A0]">
                <item.icon className="size-8" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-white">{item.title}</h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 8. FAQ
// ─────────────────────────────────────────────────────────────
const faqs = [
  {
    q: 'If there is no interest, how does the platform make money?',
    a: 'PeerPass operates on a B2B SaaS subscription model. The employer pays a flat, predictable subscription fee for access to the platform. Employees pay 0% interest, 0% commissions, and 0% platform fees.',
  },
  {
    q: 'If the employer is the guarantor, what happens if an employee defaults or leaves the company?',
    a: 'Because PeerPass is integrated directly with your payroll, contributions are deducted before the employee receives their net pay. In the rare event of sudden termination, our portal allows for final deduction processing from their final paycheck. Furthermore, our risk-mitigation algorithms cap circle amounts based on salary to protect the employer.',
  },
  {
    q: 'Will employees know who is in their Funding Circle with them?',
    a: 'Absolutely not. PeerPass uses a blind-pool system. Employees see the circle parameters (amount, duration) and join anonymously. Privacy is our core promise.',
  },
  {
    q: 'Does this require our company to lend money to employees?',
    a: 'No. The capital is generated entirely by the employees\u2019 peer pool. The employer\u2019s role is strictly to guarantee the payroll deductions and provide the secure platform. It costs your company zero dollars in lent capital.',
  },
]

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <RevealSection id="faq" className={`${sectionPy} bg-[#F8FAFC]`}>
      <div className={containerW}>
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00E5A0]">FAQ</p>
          <h2 className="mt-4 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-[#0B1120]">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="mx-auto mt-12 max-w-3xl space-y-3">
          {faqs.map((faq, i) => {
            const isOpen = openIndex === i
            return (
              <div
                key={i}
                className={`rounded-2xl border bg-white transition-all duration-200 ${
                  isOpen ? 'border-[#00E5A0]/30 shadow-lg shadow-[#00E5A0]/5' : 'border-[#E2E8F0]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 p-5 sm:p-6 text-left"
                >
                  <span className="text-sm sm:text-base font-medium text-[#0B1120]">{faq.q}</span>
                  <ChevronDownIcon
                    className={`size-5 shrink-0 text-[#64748B] transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm text-[#64748B] leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 9. FINAL CTA — with contact form
// ─────────────────────────────────────────────────────────────
function FinalCtaSection() {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [employeeCount, setEmployeeCount] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fullName.trim() || !email.trim()) {
      toast.error('Please enter your name and email')
      return
    }

    setLoading(true)
    try {
      await submitDemoRequestApi({
        full_name: fullName.trim(),
        email: email.trim(),
        company_name: companyName.trim() || undefined,
        employee_count: employeeCount ? Number(employeeCount) : undefined,
        message: message.trim() || undefined,
      })
      setSubmitted(true)
      toast.success('Demo request submitted! Our team will be in touch shortly.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit. Please try again.'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <RevealSection id="book-demo" className={`${sectionPy} bg-white`}>
      <div className={containerW}>
        <div className="relative overflow-hidden rounded-3xl bg-[#0B1120] px-6 py-16 sm:px-12 sm:py-20 lg:px-20 lg:py-28">
          {/* Decorative glow */}
          <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/8 blur-[120px]" />
          <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />

          <div className="relative z-10 mx-auto max-w-2xl">
            <h2 className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-white text-center">
              See what 0% peer funding looks like for your team.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-lg mx-auto text-center">
              In 30 minutes, we&rsquo;ll show you how your employees can borrow from each other
              at 0% interest &mdash; through the payroll you already run. No commitment.
            </p>

            {submitted ? (
              <div className="mt-10 rounded-2xl border border-[#00E5A0]/30 bg-[#00E5A0]/10 p-8 text-center">
                <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-[#00E5A0] text-[#0B1120]">
                  <CheckCircleIcon className="size-7" />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-white">Request received!</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  Thank you, {fullName.split(' ')[0]}. Our team will reach out to you at {email} within
                  one business day to schedule your demo.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false)
                    setFullName('')
                    setEmail('')
                    setCompanyName('')
                    setEmployeeCount('')
                    setMessage('')
                  }}
                  className="mt-6 text-sm text-[#00E5A0] underline underline-offset-4 hover:text-[#00E5A0]/80 transition-colors"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="mt-10 space-y-5"
                aria-label="Book a demo request form"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="demo-name" className="text-white/80">
                      Full name <span className="text-[#00E5A0]">*</span>
                    </Label>
                    <Input
                      id="demo-name"
                      type="text"
                      required
                      disabled={loading}
                      placeholder="Jane Smith"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="demo-email" className="text-white/80">
                      Work email <span className="text-[#00E5A0]">*</span>
                    </Label>
                    <Input
                      id="demo-email"
                      type="email"
                      required
                      disabled={loading}
                      placeholder="jane@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30"
                    />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="demo-company" className="text-white/80">
                      Company name
                    </Label>
                    <Input
                      id="demo-company"
                      type="text"
                      disabled={loading}
                      placeholder="Acme Inc."
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="demo-emp-count" className="text-white/80">
                      Employee count
                    </Label>
                    <Input
                      id="demo-emp-count"
                      type="number"
                      min={1}
                      disabled={loading}
                      placeholder="e.g. 250"
                      value={employeeCount}
                      onChange={(e) => setEmployeeCount(e.target.value)}
                      className="border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="demo-message" className="text-white/80">
                    Anything specific you&rsquo;d like to see? (optional)
                  </Label>
                  <Textarea
                    id="demo-message"
                    rows={3}
                    disabled={loading}
                    placeholder="Tell us about your team's needs or any questions you have..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30"
                  />
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
                  >
                    {loading ? 'Submitting…' : (
                      <>
                        Book a free walkthrough
                        <ArrowRightIcon className="ml-2 size-4" />
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-center text-xs sm:text-sm text-white/40">
                  No commitment required. We&rsquo;ll never share your information.
                </p>
              </form>
            )
            }
          </div>
        </div>
      </div>
    </RevealSection>
  )
}

// ─────────────────────────────────────────────────────────────
// 10. FOOTER
// ─────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#0B1120]">
      <div className={`${containerW} py-12 sm:py-16`}>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2.5">
              <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold">
                PP
              </div>
              <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
            </div>
            <p className="mt-4 text-sm text-white/40 leading-relaxed max-w-xs">
              The first workplace funding circle platform &mdash; anonymous, 0% interest,
              secured by your payroll.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-white/50">Product</h4>
            <ul className="mt-4 space-y-3">
              {['How It Works', 'Features', 'Security', 'FAQ'].map((item) => (
                <li key={item}>
                  <a
                    href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm text-white/60 hover:text-[#00E5A0] transition-colors"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-white/50">Company</h4>
            <ul className="mt-4 space-y-3">
              {['About', 'Blog', 'Careers'].map((item) => (
                <li key={item}>
                  <span className="text-sm text-white/40 cursor-not-allowed">{item}</span>
                </li>
              ))}
              <li>
                <Link to="/contact" className="text-sm text-white/60 hover:text-[#00E5A0] transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-white/50">Legal</h4>
            <ul className="mt-4 space-y-3">
              {[
                { to: '/privacy', label: 'Privacy Policy' },
                { to: '/terms', label: 'Terms of Service' },
                { to: '/dpa', label: 'Data Processing Agreement' },
                { to: '/cookie-policy', label: 'Cookie Policy' },
              ].map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-sm text-white/60 hover:text-[#00E5A0] transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/30">
            &copy; {new Date().getFullYear()} PeerPass. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {['Twitter', 'LinkedIn', 'GitHub'].map((item) => (
              <span
                key={item}
                className="text-xs text-white/30 cursor-not-allowed hover:text-white/50 transition-colors"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

// ─────────────────────────────────────────────────────────────
// PAGE COMPONENT
// ─────────────────────────────────────────────────────────────
function LandingPage() {
  return (
    <>
      <HeroSection />
      <ProblemSection />
      <HowItWorksSection />
      <EmployerBenefitsSection />
      <EmployeeBenefitsSection />
      <PlatformFeaturesSection />
      <SecuritySection />
      <FaqSection />
      <FinalCtaSection />
      <Footer />
    </>
  )
}

export const Route = createFileRoute('/')({
  component: LandingPage,
})
